import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Grid,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  SaveOutlined as SaveIcon,
  CheckCircleOutline,
  PersonOutline,
  BadgeOutlined,
  ScienceOutlined,
  OpacityOutlined,
  CalendarTodayOutlined,
  DescriptionOutlined,
  VisibilityOutlined,
  InfoOutlined,
  Close as CloseIcon,
} from "@mui/icons-material";
import { labResultService } from "../../services/lis";

export default function EnterResult({
  isDark,
  orderItemId,
  specimenId,
  orderId,
  onSaved,
  onCancel,
}) {
  // Theme tokens
  const cardBg = isDark ? "#1E1E1E" : "#FFFFFF";
  const innerCardBg = isDark ? "#141414" : "#F8FAFC";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0";
  const textPrimary = isDark ? "#F8FAFC" : "#0F172A";
  const textSecondary = isDark ? "#94A3B8" : "#64748B";

  // Data states
  const [meta, setMeta] = useState({
    patientName: "—",
    patientId: "—",
    sampleId: "—",
    testName: "—",
    status: "Sample Collected",
    sampleType: "Blood",
    collectionDate: "—",
    referringDoctor: "—",
    referralFileName: "",
    referralUploadedAt: "",
  });

  const [parameters, setParameters] = useState([]);
  const [interpretation, setInterpretation] = useState("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [viewReferralOpen, setViewReferralOpen] = useState(false);

  const [resolvedSpecimenId, setResolvedSpecimenId] = useState(null);
  const [resolvedOrderId, setResolvedOrderId] = useState(null);

  // Fetch actual data from backend if IDs provided
  const fetchEntryData = useCallback(async () => {
    if (!orderItemId) return;
    setLoading(true);
    setError("");
    try {
      const res = await labResultService.getForEntry(orderItemId);
      if (res?.data?.data) {
        const data = res.data.data;
        const ord = data.order || {};
        const pt = ord.patient || ord.patientId || {};
        const sp = data.specimen || {};

        const effectiveSpecimenId =
          sp._id ||
          data.specimen?._id ||
          data.orderItem?.specimen ||
          specimenId;
        const effectiveOrderId =
          ord._id ||
          data.order?._id ||
          data.orderItem?.labOrder ||
          orderId;

        if (effectiveSpecimenId) setResolvedSpecimenId(effectiveSpecimenId);
        if (effectiveOrderId) setResolvedOrderId(effectiveOrderId);

        setMeta({
          patientName: pt.firstName
            ? `${pt.firstName} ${pt.lastName || ""}`
            : ord.patientName || "Patient",
          patientId: pt.patientId || pt.mrn || ord.patientMrn || "—",
          sampleId:
            sp.sampleBarcode ||
            sp.sampleId ||
            sp.specimenId ||
            data.sampleInfo?.sampleId ||
            "—",
          testName:
            data.test?.displayName ||
            data.test?.testName ||
            "Laboratory Test",
          status:
            sp.status === "COLLECTED" ? "Sample Collected" : "In Progress",
          sampleType:
            sp.specimenType || data.test?.specimenType || "Blood",
          collectionDate: sp.collectedAt || sp.collectionTimestamp
            ? new Date(
                sp.collectedAt || sp.collectionTimestamp
              ).toLocaleString("en-US", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "—",
          referringDoctor:
            ord.requestingDoctor || ord.referringDoctor || "—",
          hasReferralDoc: Boolean(
            (ord.referralDocument && (ord.referralDocument.url || ord.referralDocument.filename)) ||
            ord.referralFileUrl
          ),
          referralFileName:
            ord.referralDocument?.filename ||
            ord.referralFileName ||
            "",
          referralFileUrl:
            ord.referralDocument?.url || ord.referralFileUrl || "",
          referralUploadedAt: ord.referralDocument?.uploadedAt || ord.createdAt
            ? new Date(ord.referralDocument?.uploadedAt || ord.createdAt).toLocaleString("en-US", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "",
        });

        if (res.data.isExisting) {
          setParameters(
            (data.parameterResults || []).map((p) => ({
              ...p,
              value: p.value || "",
              referenceRangeText: p.referenceRangeText || p.rangeText || p.referenceRange || "",
            }))
          );
          if (data.interpretationText) {
            setInterpretation(data.interpretationText);
          }
        } else if (
          data.parameterTemplate &&
          data.parameterTemplate.length > 0
        ) {
          setParameters(
            data.parameterTemplate.map((p) => ({
              parameterName: p.parameterName,
              parameterCode: p.parameterCode,
              value: "",
              unit: p.unit,
              referenceRangeText: p.referenceRangeText,
              description: p.description,
            }))
          );
        }
      }
    } catch (err) {
      setError(
        err?.response?.data?.message || "Failed to load test parameters"
      );
    } finally {
      setLoading(false);
    }
  }, [orderItemId, specimenId, orderId]);

  useEffect(() => {
    fetchEntryData();
  }, [fetchEntryData]);

  // Update value in table
  const handleValueChange = (index, val) => {
    setParameters((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], value: val };
      return next;
    });
  };

  // Save (Draft or Complete)
  const handleSave = async (isDraft) => {
    setError("");
    setSuccess("");

    const finalSpecimenId = specimenId || resolvedSpecimenId;
    const finalOrderId = orderId || resolvedOrderId;

    if (!finalSpecimenId || !finalOrderId) {
      setError("Specimen or Order ID could not be resolved. Please refresh and try again.");
      return;
    }

    setSaving(true);
    try {
      await labResultService.saveResult({
        specimenId: finalSpecimenId,
        labOrderId: finalOrderId,
        labOrderItemId: orderItemId,
        parameterResults: parameters.map((p) => ({
          parameterName: p.parameterName,
          parameterCode: p.parameterCode,
          value: p.value,
          unit: p.unit,
        })),
        interpretationText: interpretation,
        isDraft,
      });

      setSuccess(
        isDraft
          ? "Draft saved successfully!"
          : "Results completed and report generated successfully!"
      );
      setTimeout(() => onSaved?.(), 1000);
    } catch (err) {
      console.error("Failed to save results:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save results"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: "100%", pb: 3 }}>
      {/* Top Back Link */}
      <Box sx={{ mb: 1 }}>
        <Button
          onClick={onCancel}
          startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: "none",
            fontFamily: "'Satoshi', sans-serif",
            fontWeight: 700,
            fontSize: "0.82rem",
            color: "#2563EB",
            p: 0,
            "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
          }}
        >
          Back to Results
        </Button>
      </Box>

      {/* Page Title */}
      <Typography
        sx={{
          fontFamily: "'Satoshi', sans-serif",
          fontSize: { xs: "1.2rem", md: "1.35rem" },
          fontWeight: 800,
          color: textPrimary,
          lineHeight: 1.2,
          letterSpacing: "-0.02em",
          mb: 1.5,
        }}
      >
        Enter Result
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.5, borderRadius: "8px", fontSize: "0.82rem" }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 1.5, py: 0.5, borderRadius: "8px", fontSize: "0.82rem" }}>
          {success}
        </Alert>
      )}

      {/* 1. TOP PATIENT / SAMPLE META STRIP CARD */}
      <Paper
        elevation={0}
        sx={{
          p: 1.25,
          px: 2,
          borderRadius: "10px",
          bgcolor: cardBg,
          border: `1px solid ${borderColor}`,
          boxShadow: isDark
            ? "0 2px 8px rgba(0,0,0,0.25)"
            : "0 2px 8px rgba(15, 23, 42, 0.03)",
          mb: 1.5,
        }}
      >
        <Grid container spacing={1.5} alignItems="center">
          {/* Patient Name */}
          <Grid item xs={6} sm={4} md={2.4}>
            <Stack direction="row" spacing={1} alignItems="center">
              <PersonOutline sx={{ color: textSecondary, fontSize: 18 }} />
              <Box>
                <Typography sx={{ fontSize: "0.68rem", color: textSecondary, fontWeight: 500, lineHeight: 1 }}>
                  Patient
                </Typography>
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: textPrimary, mt: 0.2 }}>
                  {meta.patientName}
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Patient ID */}
          <Grid item xs={6} sm={4} md={2.4}>
            <Stack direction="row" spacing={1} alignItems="center">
              <BadgeOutlined sx={{ color: textSecondary, fontSize: 18 }} />
              <Box>
                <Typography sx={{ fontSize: "0.68rem", color: textSecondary, fontWeight: 500, lineHeight: 1 }}>
                  Patient ID
                </Typography>
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: textPrimary, mt: 0.2 }}>
                  {meta.patientId}
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Sample ID */}
          <Grid item xs={6} sm={4} md={2.4}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ScienceOutlined sx={{ color: textSecondary, fontSize: 18 }} />
              <Box>
                <Typography sx={{ fontSize: "0.68rem", color: textSecondary, fontWeight: 500, lineHeight: 1 }}>
                  Sample ID
                </Typography>
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: textPrimary, mt: 0.2 }}>
                  {meta.sampleId}
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Test */}
          <Grid item xs={6} sm={4} md={2.8}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ScienceOutlined sx={{ color: textSecondary, fontSize: 18 }} />
              <Box>
                <Typography sx={{ fontSize: "0.68rem", color: textSecondary, fontWeight: 500, lineHeight: 1 }}>
                  Test
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    color: textPrimary,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    mt: 0.2,
                  }}
                >
                  {meta.testName}
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Status */}
          <Grid item xs={12} sm={4} md={2}>
            <Box>
              <Typography sx={{ fontSize: "0.68rem", color: textSecondary, fontWeight: 500, mb: 0.2, lineHeight: 1 }}>
                Status
              </Typography>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.6,
                  px: 1,
                  py: 0.2,
                  borderRadius: "5px",
                  bgcolor: isDark ? "rgba(217, 119, 6, 0.15)" : "#FFFBEB",
                  border: `1px solid ${isDark ? "rgba(217, 119, 6, 0.3)" : "#FEF3C7"}`,
                  color: "#D97706",
                  fontSize: "0.72rem",
                  fontWeight: 700,
                }}
              >
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    bgcolor: "#D97706",
                  }}
                />
                {meta.status}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* 2. SAMPLE INFORMATION CARD */}
      <Box sx={{ mb: 1.5 }}>
        <Typography
          sx={{
            fontFamily: "'Satoshi', sans-serif",
            fontSize: "0.88rem",
            fontWeight: 700,
            color: textPrimary,
            mb: 0.8,
          }}
        >
          Sample Information
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: 1.25,
            px: 2,
            borderRadius: "10px",
            bgcolor: cardBg,
            border: `1px solid ${borderColor}`,
            boxShadow: isDark
              ? "0 2px 8px rgba(0,0,0,0.25)"
              : "0 2px 8px rgba(15, 23, 42, 0.03)",
          }}
        >
          <Grid container spacing={2}>
            {/* Sample Type */}
            <Grid item xs={12} sm={4}>
              <Stack direction="row" spacing={1} alignItems="center">
                <OpacityOutlined sx={{ color: textSecondary, fontSize: 18 }} />
                <Box>
                  <Typography sx={{ fontSize: "0.68rem", color: textSecondary, fontWeight: 500, lineHeight: 1 }}>
                    Sample Type
                  </Typography>
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: textPrimary, mt: 0.2 }}>
                    {meta.sampleType}
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            {/* Collection Date */}
            <Grid item xs={12} sm={4}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarTodayOutlined sx={{ color: textSecondary, fontSize: 18 }} />
                <Box>
                  <Typography sx={{ fontSize: "0.68rem", color: textSecondary, fontWeight: 500, lineHeight: 1 }}>
                    Collection Date
                  </Typography>
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: textPrimary, mt: 0.2 }}>
                    {meta.collectionDate}
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            {/* Referring Doctor */}
            <Grid item xs={12} sm={4}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PersonOutline sx={{ color: textSecondary, fontSize: 18 }} />
                <Box>
                  <Typography sx={{ fontSize: "0.68rem", color: textSecondary, fontWeight: 500, lineHeight: 1 }}>
                    Referring Doctor
                  </Typography>
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: textPrimary, mt: 0.2 }}>
                    {meta.referringDoctor}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* 3. UPLOADED REFERRAL CARD — Only rendered if a document was uploaded */}
      {meta.hasReferralDoc && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontSize: "0.88rem",
              fontWeight: 700,
              color: textPrimary,
              mb: 0.8,
            }}
          >
            Uploaded Referral Document
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: 1.25,
              px: 2,
              borderRadius: "10px",
              bgcolor: cardBg,
              border: `1px solid ${borderColor}`,
              boxShadow: isDark
                ? "0 2px 8px rgba(0,0,0,0.25)"
                : "0 2px 8px rgba(15, 23, 42, 0.03)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "6px",
                  bgcolor: isDark ? "rgba(217, 119, 6, 0.15)" : "#FEF3C7",
                  color: isDark ? "#FBBF24" : "#D97706",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: isDark ? "1px solid rgba(217, 119, 6, 0.3)" : "1px solid #FDE68A",
                }}
              >
                <DescriptionOutlined sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "0.82rem", color: textPrimary }}>
                  {meta.referralFileName || "Referral Document"}
                </Typography>
                {meta.referralUploadedAt && (
                  <Typography sx={{ fontSize: "0.68rem", color: textSecondary, mt: 0.2 }}>
                    Uploaded on {meta.referralUploadedAt}
                  </Typography>
                )}
              </Box>
            </Box>

            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                if (meta.referralFileUrl) {
                  window.open(meta.referralFileUrl, "_blank");
                } else {
                  setViewReferralOpen(true);
                }
              }}
              startIcon={<VisibilityOutlined sx={{ fontSize: 15 }} />}
              sx={{
                borderRadius: "6px",
                borderColor: "#3B82F6",
                color: "#2563EB",
                textTransform: "none",
                fontFamily: "'Satoshi', sans-serif",
                fontWeight: 600,
                fontSize: "0.74rem",
                px: 1.2,
                py: 0.3,
                height: 26,
                "&:hover": {
                  borderColor: "#1D4ED8",
                  bgcolor: isDark ? "rgba(37,99,235,0.1)" : "#EFF6FF",
                },
              }}
            >
              View Document
            </Button>
          </Paper>
        </Box>
      )}

      {/* 4. RESULT ENTRY TABLE CARD */}
      <Box sx={{ mb: 1.5 }}>
        <Typography
          sx={{
            fontFamily: "'Satoshi', sans-serif",
            fontSize: "0.88rem",
            fontWeight: 700,
            color: textPrimary,
            mb: 0.8,
          }}
        >
          Result Entry
        </Typography>

        <Paper
          elevation={0}
          sx={{
            borderRadius: "10px",
            bgcolor: cardBg,
            border: `1px solid ${borderColor}`,
            boxShadow: isDark
              ? "0 2px 8px rgba(0,0,0,0.25)"
              : "0 2px 8px rgba(15, 23, 42, 0.04)",
            overflow: "hidden",
          }}
        >
          {/* Subheader */}
          <Box sx={{ p: 1.25, px: 2, borderBottom: `1px solid ${borderColor}` }}>
            <Typography
              sx={{
                fontFamily: "'Satoshi', sans-serif",
                fontSize: "0.85rem",
                fontWeight: 700,
                color: textPrimary,
              }}
            >
              {meta.testName}
            </Typography>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: isDark ? "#171717" : "#F8FAFC" }}>
                  <TableCell
                    sx={{
                      fontFamily: "'Satoshi', sans-serif",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      color: textSecondary,
                      py: "5px !important",
                      px: "8px !important",
                      borderBottom: `1px solid ${borderColor}`,
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                    }}
                  >
                    Parameter
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "'Satoshi', sans-serif",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      color: textSecondary,
                      py: "5px !important",
                      px: "8px !important",
                      borderBottom: `1px solid ${borderColor}`,
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                    }}
                  >
                    Result <Box component="span" sx={{ color: "#EF4444" }}>*</Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "'Satoshi', sans-serif",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      color: textSecondary,
                      py: "5px !important",
                      px: "8px !important",
                      borderBottom: `1px solid ${borderColor}`,
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                    }}
                  >
                    Unit
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: "'Satoshi', sans-serif",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      color: textSecondary,
                      py: "5px !important",
                      px: "8px !important",
                      borderBottom: `1px solid ${borderColor}`,
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                    }}
                  >
                    Reference Range
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {parameters.map((param, index) => (
                  <TableRow
                    key={param.parameterCode || index}
                    sx={{
                      "&:last-child td": { borderBottom: 0 },
                      "&:hover": {
                        bgcolor: isDark
                          ? "rgba(255,255,255,0.02)"
                          : "rgba(241,245,249,0.5)",
                      },
                    }}
                  >
                    {/* Parameter with Info Icon */}
                    <TableCell
                      sx={{
                        fontFamily: "'Satoshi', sans-serif",
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        color: textPrimary,
                        px: "8px !important",
                        py: "3px !important",
                        borderBottom: `1px solid ${borderColor}`,
                      }}
                    >
                      <Stack direction="row" spacing={0.6} alignItems="center">
                        <Typography
                          sx={{
                            fontFamily: "'Satoshi', sans-serif",
                            fontWeight: 600,
                            fontSize: "0.8rem",
                            color: textPrimary,
                          }}
                        >
                          {param.parameterName}
                        </Typography>
                        <Tooltip
                          title={
                            param.description ||
                            (param.referenceRangeText || param.rangeText || param.referenceRange
                              ? `${param.parameterName} reference range: ${param.referenceRangeText || param.rangeText || param.referenceRange} ${param.unit || ""}`.trim()
                              : param.unit
                              ? `${param.parameterName} (Unit: ${param.unit})`
                              : `${param.parameterName}`)
                          }
                        >
                          <InfoOutlined sx={{ fontSize: 14, color: textSecondary, cursor: "pointer" }} />
                        </Tooltip>
                      </Stack>
                    </TableCell>

                    {/* Result Input Box */}
                    <TableCell
                      sx={{
                        px: "8px !important",
                        py: "3px !important",
                        borderBottom: `1px solid ${borderColor}`,
                      }}
                    >
                      <TextField
                        size="small"
                        value={param.value}
                        onChange={(e) => handleValueChange(index, e.target.value)}
                        placeholder="Enter value"
                        sx={{
                          width: { xs: 110, sm: 140 },
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "6px",
                            fontFamily: "'Satoshi', sans-serif",
                            fontSize: "0.82rem",
                            fontWeight: 600,
                            bgcolor: isDark ? "#141414" : "#FFFFFF",
                            "& fieldset": { borderColor },
                            "&:hover fieldset": { borderColor: "#3B82F6" },
                            "&.Mui-focused fieldset": { borderColor: "#2563EB" },
                          },
                          "& input": {
                            py: "4px !important",
                            px: "8px !important",
                          },
                        }}
                      />
                    </TableCell>

                    {/* Unit */}
                    <TableCell
                      sx={{
                        fontFamily: "'Satoshi', sans-serif",
                        fontSize: "0.78rem",
                        color: textSecondary,
                        px: "8px !important",
                        py: "3px !important",
                        borderBottom: `1px solid ${borderColor}`,
                      }}
                    >
                      {param.unit || "—"}
                    </TableCell>

                    {/* Reference Range */}
                    <TableCell
                      sx={{
                        fontFamily: "'Satoshi', sans-serif",
                        fontSize: "0.78rem",
                        color: textSecondary,
                        px: "8px !important",
                        py: "3px !important",
                        borderBottom: `1px solid ${borderColor}`,
                      }}
                    >
                      {param.referenceRangeText || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* 5. INTERPRETATION CARD */}
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontFamily: "'Satoshi', sans-serif",
            fontSize: "0.88rem",
            fontWeight: 700,
            color: textPrimary,
            mb: 0.8,
          }}
        >
          Interpretation <Typography component="span" sx={{ fontSize: "0.76rem", color: textSecondary, fontWeight: 400 }}>(Optional)</Typography>
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: 1.25,
            px: 2,
            borderRadius: "10px",
            bgcolor: cardBg,
            border: `1px solid ${borderColor}`,
            boxShadow: isDark
              ? "0 2px 8px rgba(0,0,0,0.25)"
              : "0 2px 8px rgba(15, 23, 42, 0.03)",
          }}
        >
          <TextField
            fullWidth
            multiline
            rows={2}
            value={interpretation}
            onChange={(e) => {
              if (e.target.value.length <= 500) setInterpretation(e.target.value);
            }}
            placeholder="Enter clinical interpretation..."
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                fontFamily: "'Satoshi', sans-serif",
                fontSize: "0.82rem",
                bgcolor: isDark ? "#141414" : "#FFFFFF",
                "& fieldset": { borderColor },
                "&:hover fieldset": { borderColor: "#3B82F6" },
              },
            }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.6 }}>
            <Typography sx={{ fontSize: "0.68rem", color: textSecondary, fontFamily: "'Satoshi', sans-serif" }}>
              {interpretation.length} / 500
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* 6. BOTTOM ACTION BUTTONS */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
        {/* Save Draft */}
        <Button
          variant="outlined"
          onClick={() => handleSave(true)}
          disabled={saving}
          startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
          sx={{
            borderRadius: "6px",
            borderColor: "#3B82F6",
            color: "#2563EB",
            textTransform: "none",
            fontFamily: "'Satoshi', sans-serif",
            fontWeight: 600,
            fontSize: "0.8rem",
            px: 2,
            py: 0.5,
            height: 30,
            "&:hover": {
              borderColor: "#1D4ED8",
              bgcolor: isDark ? "rgba(37,99,235,0.1)" : "#EFF6FF",
            },
          }}
        >
          Save Draft
        </Button>

        {/* Complete Result */}
        <Button
          variant="contained"
          onClick={() => handleSave(false)}
          disabled={saving}
          startIcon={!saving && <CheckCircleOutline sx={{ fontSize: 16 }} />}
          sx={{
            borderRadius: "6px",
            bgcolor: "#059669",
            color: "#FFFFFF",
            textTransform: "none",
            fontFamily: "'Satoshi', sans-serif",
            fontWeight: 700,
            fontSize: "0.8rem",
            px: 2.2,
            py: 0.5,
            height: 30,
            boxShadow: "0 2px 8px rgba(5, 150, 105, 0.25)",
            "&:hover": {
              bgcolor: "#047857",
              boxShadow: "0 4px 12px rgba(5, 150, 105, 0.35)",
            },
            "&.Mui-disabled": {
              bgcolor: isDark ? "#2D3748" : "#E2E8F0",
              color: isDark ? "#718096" : "#94A3B8",
            },
          }}
        >
          {saving ? (
            <CircularProgress size={18} sx={{ color: "#ffffff" }} />
          ) : (
            "Complete Result"
          )}
        </Button>
      </Box>

      {/* View Referral Modal */}
      <Dialog
        open={viewReferralOpen}
        onClose={() => setViewReferralOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: "95vw", sm: "520px" },
            maxWidth: "540px !important",
            maxHeight: "85vh",
            bgcolor: cardBg,
            borderRadius: "14px",
            border: `1px solid ${borderColor}`,
            p: 0,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2.5,
            py: 1.25,
            borderBottom: `1px solid ${borderColor}`,
            fontFamily: "'Satoshi', sans-serif",
            fontWeight: 700,
            color: textPrimary,
            fontSize: "1.05rem",
          }}
        >
          <span>{meta.referralFileName || "Referral Document"}</span>
          <IconButton
            size="small"
            onClick={() => setViewReferralOpen(false)}
            sx={{
              color: textSecondary,
              "&:hover": {
                bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                color: textPrimary,
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Box
            sx={{
              p: 2.5,
              textAlign: "center",
              bgcolor: isDark ? "#141414" : "#F8FAFC",
              borderRadius: "10px",
              border: `1px dashed ${borderColor}`,
            }}
          >
            <DescriptionOutlined sx={{ fontSize: 40, color: "#9333EA", mb: 0.8 }} />
            <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: textPrimary, fontFamily: "'Satoshi', sans-serif" }}>
              Clinical Referral Letter & Diagnostics Order
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: textSecondary, mt: 0.5 }}>
              Patient: {meta.patientName} ({meta.patientId}) • Referring: {meta.referringDoctor}
            </Typography>
            {meta.clinicalNotes ? (
              <Typography sx={{ fontSize: "0.8rem", color: textPrimary, mt: 1.2, fontWeight: 500, bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#FFFFFF", p: 1, borderRadius: "6px", border: `1px solid ${borderColor}`, textAlign: "left" }}>
                <strong>Clinical Notes:</strong> {meta.clinicalNotes}
              </Typography>
            ) : (
              <Typography sx={{ fontSize: "0.75rem", color: textSecondary, mt: 1, fontStyle: "italic" }}>
                No clinical notes attached to this referral.
              </Typography>
            )}
            {meta.referralFileUrl && (
              <Button
                variant="outlined"
                href={meta.referralFileUrl}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{
                  mt: 1.8,
                  textTransform: "none",
                  borderRadius: "8px",
                  borderColor: "#9333EA",
                  color: "#9333EA",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  py: 0.6,
                  "&:hover": {
                    borderColor: "#7E22CE",
                    bgcolor: "rgba(147,51,234,0.05)",
                  },
                }}
              >
                View Uploaded Document ({meta.referralFileName})
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${borderColor}` }}>
          <Button
            onClick={() => setViewReferralOpen(false)}
            size="small"
            sx={{
              color: textSecondary,
              textTransform: "none",
              fontFamily: "'Satoshi', sans-serif",
              fontWeight: 600,
              fontSize: "0.82rem",
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
